import { ProcessingJobType } from "@prisma/client";
import { useQuery } from "@tanstack/react-query";
import { getRunningJobs } from "@/app/actions/user/get-running-jobs/action";
import { UserStore } from "@/app/stores/UserStore";
import { ProcessingJobData } from "@/app/types/job/ProcessingJob";
import { Globals } from "@/app/vars/Globals";
import { QueryKey } from "@/app/vars/QueryKeys";

const MESSAGE_JOB_TYPES: ProcessingJobType[] = [
  ProcessingJobType.MESSAGE_SEQUENCE,
  ProcessingJobType.MESSAGE_SEQUENCE_STEP,
];

export function useFetchRunningJobs(options: { enabled?: boolean } = {}) {
  const { enabled = true } = options;
  const user = UserStore((state) => state.user);

  return useQuery({
    queryKey: [QueryKey.RUNNING_JOBS],
    queryFn: async () => {
      return await getRunningJobs();
    },
    enabled: enabled && !!user,
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchInterval: (query) => {
      const jobs = query.state.data as ProcessingJobData[] | undefined;

      // Poll while any job is running, on any page, so the UI detects completion and
      // fires the job-done invalidations. Cadence follows the running job type.
      if (!jobs || jobs.length === 0) return false;
      // Rewriting one variant is the shortest job of all: on the message cadence its loader
      // outlives the work it stands for.
      const hasVariantJob = jobs.some(
        (job) => job.type === ProcessingJobType.MESSAGE_SEQUENCE_STEP,
      );
      if (hasVariantJob) return Globals.VARIANT_JOB_POLLING_INTERVAL_MS;
      const hasProspectJob = jobs.some((job) => job.type === ProcessingJobType.PROSPECT_SEARCH);
      if (hasProspectJob) return Globals.PROSPECT_JOB_POLLING_INTERVAL_MS;
      // Message jobs take seconds, not minutes: on the default cadence the UI would sit on
      // the old content for up to a minute after the work was already done.
      const hasMessageJob = jobs.some((job) => MESSAGE_JOB_TYPES.includes(job.type));
      if (hasMessageJob) return Globals.MESSAGE_JOB_POLLING_INTERVAL_MS;
      const hasAvatarJob = jobs.some((job) => job.type === ProcessingJobType.CLIENT_AVATAR);
      return hasAvatarJob
        ? Globals.AVATAR_JOB_POLLING_INTERVAL_MS
        : Globals.ACTIVE_JOB_POLLING_INTERVAL_MS;
    },
  });
}
